import type { APIClient } from '@src/shared/api';

import { useMutation, useQuery } from '@src/shared/query';

export type PasswordChangePayload = {
  otp_code: string;
  password: string;
};

type PasswordChangeResponse = {
  detail: string;
  access: string;
  refresh: string;
};

type UserPasswordResetResponse = {
  detail: string;
  access?: string;
  refresh?: string;
};

type UserTwoFactorResetPayload = {
  admin_otp_code: string;
  id: number | string;
};

type UserTwoFactorResetResponse = {
  detail: string;
};

export type UserTwoFactorConfiguration = {
  challenge_token: string | null;
  enabled: boolean;
  manual_entry_key: string;
  qr_code_data_url: string;
};

export type UserTwoFactorStatus = Pick<UserTwoFactorConfiguration, 'enabled'>;

type UserTwoFactorEnrollmentPayload = {
  challenge_token: string;
  id: number | string;
  otp_code: string;
};

export function usePasswordChange() {
  return useMutation<PasswordChangeResponse, Error, PasswordChangePayload>({
    mutationKey: ['auth', 'password-change'],
    mutateFn: ({
      apiClient,
      ...payload
    }: { apiClient: APIClient } & Record<string, unknown>) => {
      return apiClient.request({
        url: `${import.meta.env.VITE_AUTH_DOMAIN}/api/v1/auth/password/change/`,
        method: 'POST',
        payload,
      }) as Promise<PasswordChangeResponse>;
    },
  });
}

type UserPasswordResetPayload = {
  admin_otp_code: string;
  id: number | string;
  password: string;
};

export function useUserPasswordReset() {
  return useMutation<
    UserPasswordResetResponse,
    Error,
    UserPasswordResetPayload
  >({
    mutationKey: ['users', 'password-reset'],
    mutateFn: ({ admin_otp_code, apiClient, id, password }) => {
      return apiClient.request({
        url: `users/${String(id)}/password/reset/`,
        method: 'POST',
        payload: { admin_otp_code, password },
      }) as Promise<UserPasswordResetResponse>;
    },
  });
}

export function useUserTwoFactorReset() {
  return useMutation<
    UserTwoFactorResetResponse,
    Error,
    UserTwoFactorResetPayload
  >({
    mutationKey: ['users', 'two-factor-reset'],
    mutateFn: ({ admin_otp_code, apiClient, id }) => {
      return apiClient.request({
        url: `users/${String(id)}/two-factor/reset/`,
        method: 'POST',
        payload: { admin_otp_code },
      }) as Promise<UserTwoFactorResetResponse>;
    },
  });
}

export function useUserTwoFactorRegeneration() {
  return useMutation<
    UserTwoFactorConfiguration,
    Error,
    UserTwoFactorResetPayload
  >({
    mutationKey: ['users', 'two-factor-regeneration'],
    mutateFn: ({ admin_otp_code, apiClient, id }) => {
      return apiClient.request({
        url: `users/${String(id)}/two-factor/regenerate/`,
        method: 'POST',
        payload: { admin_otp_code },
      }) as Promise<UserTwoFactorConfiguration>;
    },
  });
}

export function useUserTwoFactorConfiguration(
  id: number | string,
  enabled: boolean,
) {
  return useQuery<UserTwoFactorStatus>(`users/${String(id)}/two-factor/`, {
    enabled,
  });
}

export function useUserTwoFactorReveal() {
  return useMutation<
    UserTwoFactorConfiguration,
    Error,
    UserTwoFactorResetPayload
  >({
    mutationKey: ['users', 'two-factor-reveal'],
    mutateFn: ({ admin_otp_code, apiClient, id }) => {
      return apiClient.request({
        url: `users/${String(id)}/two-factor/reveal/`,
        method: 'POST',
        payload: { admin_otp_code },
      }) as Promise<UserTwoFactorConfiguration>;
    },
  });
}

type UserTwoFactorEnrollmentCancelPayload = {
  challenge_token: string;
  id: number | string;
};

export function useUserTwoFactorEnrollmentCancel() {
  return useMutation<void, Error, UserTwoFactorEnrollmentCancelPayload>({
    mutationKey: ['users', 'two-factor-enrollment-cancel'],
    mutateFn: ({ apiClient, id, challenge_token }) => {
      return apiClient.request({
        url: `users/${String(id)}/two-factor/cancel/`,
        method: 'POST',
        payload: { challenge_token },
      }) as Promise<void>;
    },
  });
}

export function useUserTwoFactorEnrollment() {
  return useMutation<
    UserTwoFactorStatus,
    Error,
    UserTwoFactorEnrollmentPayload
  >({
    mutationKey: ['users', 'two-factor-enrollment'],
    mutateFn: ({ apiClient, id, challenge_token, otp_code }) => {
      return apiClient.request({
        url: `users/${String(id)}/two-factor/confirm/`,
        method: 'POST',
        payload: { challenge_token, otp_code },
      }) as Promise<UserTwoFactorStatus>;
    },
  });
}
